// src/components/modal/AircraftExport.js
// Export PDF - WHITE BACKGROUND with BLACK TEXT (USA units)

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Formatters (US Units) ───────────────────────────────────────────────────

const fmt$    = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtGal  = (v) => (v == null ? '—' : `${Number(v).toFixed(1)} gal`);
const fmtLbs  = (lbs) => {
  if (lbs == null) return '—';
  const num = Number(lbs);
  return isNaN(num) ? '—' : `${Math.round(num).toLocaleString()} lbs`;
};
const fmtDT   = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// ─── jsPDF CDN loader ─────────────────────────────────────────────────────────

const loadJsPDF = () => new Promise((resolve, reject) => {
  if (window.jspdf?.jsPDF) { resolve(window.jspdf.jsPDF); return; }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  s.onload  = () => window.jspdf?.jsPDF ? resolve(window.jspdf.jsPDF) : reject(new Error('jsPDF missing'));
  s.onerror = () => reject(new Error('CDN load failed'));
  document.head.appendChild(s);
});

// ─── Logo → base64 ───────────────────────────────────────────────────────────

const logoToBase64 = async (logoUrl) => {
  if (!logoUrl) return null;
  const url = logoUrl.startsWith('http') ? logoUrl : `${API_BASE}${logoUrl}`;
  try {
    const res   = await axios.get(url, { responseType: 'arraybuffer' });
    const bytes = new Uint8Array(res.data);
    let bin = '';
    bytes.forEach(b => { bin += String.fromCharCode(b); });
    return btoa(bin);
  } catch { return null; }
};

// ─── PDF (WHITE BACKGROUND + BLACK TEXT) ──────────────────────────────────────

export const exportPDF = async (data, company) => {
  const JsPDF = await loadJsPDF();
  const doc   = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const W  = 210;
  const M  = 14;
  const R  = W - M;
  const now     = new Date();
  const genStr  = now.toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const ac = data.aircraft    || {};
  const o  = data.owner       || {};
  const fs = data.fee_summary || {};

  // ============================================================
  // FONCTION POUR DESSINER HEADER/FOOTER (sans effacer le contenu)
  // ============================================================
  const drawHeaderFooter = (pageNum, total) => {
    // Sauvegarder la position actuelle
    const currentY = doc.getCurrentPageInfo()?.pageContext?.y;
    
    // Dessiner le HEADER (en haut de page)
    doc.setFillColor(255, 255, 255);
    // Ne pas effacer tout, juste dessiner par-dessus
    
    // Logo
    let logoW = 0;
    if (company._logoBase64) {
      try {
        doc.addImage(company._logoBase64, 'PNG', M, 8, 28, 18);
        logoW = 32;
      } catch { }
    }

    // FBO name - BLACK
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(company.name || 'FBO', M + logoW, 14);

    const sub = [company.icao_code, company.city, company.state].filter(Boolean).join('  ·  ');
    if (sub) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(sub, M + logoW, 19.5);
    }

    // Contacts - BLACK
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    const contacts = [
      company.address,
      [company.city, company.state, company.zip_code].filter(Boolean).join(', '),
      company.phone,
      company.email,
    ].filter(Boolean);
    contacts.forEach((line, i) => {
      doc.text(line, R, 9 + i * 4.5, { align: 'right' });
    });

    // Separator
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(M, 30, R, 30);

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Aircraft Detail Report — ${ac.tail_number || ''}`, M, 37);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated: ${genStr}`, R, 37, { align: 'right' });
    doc.line(M, 40, R, 40);

    // FOOTER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.line(M, 283, R, 283);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated by AeroTrick for ${company.name || 'FBO'} · © ${now.getFullYear()} AeroTrick. All rights reserved.`, M, 289);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${pageNum} / ${total}`, R, 289, { align: 'right' });
  };

  // ============================================================
  // DESSINER LE CONTENU
  // ============================================================
  
  // Commencer à la position y = 44
  let y = 44;
  
  // Section title
  const section = (title) => {
    if (y > 265) { 
      doc.addPage(); 
      y = 44; 
      drawHeaderFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
    }
    y += 4;
    doc.setFillColor(240, 240, 240);
    doc.rect(M, y, R - M, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(title.toUpperCase(), M + 3, y + 5);
    y += 9;
  };

  // Key-Value row
  const kv = (l1, v1, l2 = null, v2 = null) => {
    if (y > 270) { 
      doc.addPage(); 
      y = 44; 
      drawHeaderFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(0, 0, 0);
    // Left
    doc.text(l1, M, y);
    doc.text(String(v1 ?? '—'), M + 80, y, { align: 'right' });
    // Right
    if (l2 !== null) {
      doc.text(l2, M + 95, y);
      doc.text(String(v2 ?? '—'), R, y, { align: 'right' });
    }
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(M, y + 1.5, R, y + 1.5);
    y += 5.5;
  };

  // Table
  const table = (headers, rows, widths) => {
    const totalW = widths.reduce((a, b) => a + b, 0);
    
    // Header
    if (y > 265) {
      doc.addPage();
      y = 44;
      drawHeaderFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
    }
    doc.setFillColor(230, 230, 230);
    doc.rect(M, y, totalW, 6.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    let cx = M + 2;
    headers.forEach((h, i) => {
      doc.text(h, cx, y + 4.5);
      cx += widths[i];
    });
    y += 6.5;

    rows.forEach((row, idx) => {
      if (y > 270) {
        doc.addPage();
        y = 44;
        drawHeaderFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
        // Redessiner header du tableau
        doc.setFillColor(230, 230, 230);
        doc.rect(M, y, totalW, 6.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        cx = M + 2;
        headers.forEach((h, i) => {
          doc.text(h, cx, y + 4.5);
          cx += widths[i];
        });
        y += 6.5;
      }
      
      if (idx % 2 === 1) {
        doc.setFillColor(248, 248, 248);
        doc.rect(M, y, totalW, 5.5, 'F');
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      cx = M + 2;
      row.forEach((cell, ci) => {
        const text = String(cell ?? '—');
        doc.text(text.length > 35 ? text.substring(0, 34) + '…' : text, cx, y + 4);
        cx += widths[ci];
      });
      y += 5.5;
    });
    y += 4;
  };

  // ============================================================
  // DESSINER LA PREMIÈRE PAGE HEADER/FOOTER
  // ============================================================
  drawHeaderFooter(1, 1);

  // ============================================================
  // CONTENU
  // ============================================================

  // Aircraft
  section('AIRCRAFT INFORMATION');
  kv('Tail Number',   ac.tail_number,   'Status',       data.source === 'registered' ? 'Registered Client' : 'Visitor');
  kv('Aircraft Type', ac.aircraft_type, 'Manufacturer', ac.manufacturer);
  kv('Model',         ac.model,         'Engine Type',  ac.engine_type);
  kv('MTOW (lbs)',    fmtLbs(ac.mtow_kg), 'Weight Class', ac.weight_class || '—');
  kv('Callsign',      ac.callsign,      'Home Base',    ac.home_base);
  if (data.source === 'visitor') {
    kv('Operator',   ac.operator_name,          'Fuel Added', ac.fuel_added ? fmtGal(ac.fuel_added) : '—');
    kv('First Seen', fmtDT(ac.first_seen),      'Last Seen',  fmtDT(ac.last_seen));
  }

  // Owner
  if (data.source === 'registered' && Object.keys(o).length > 0) {
    section('OWNER / CLIENT');
    const name = o.company || `${o.first_name || ''} ${o.last_name || ''}`.trim();
    kv('Name / Company', name, 'Email', o.email);
    kv('Phone', o.phone_mobile || o.phone, 'Location', [o.city, o.state].filter(Boolean).join(', '));
    kv('Landing Fee',    o.charge_landing_fees ? 'Charged' : 'EXEMPT',
       'Takeoff Fee',    o.charge_takeoff_fees  ? 'Charged' : 'EXEMPT');
    kv('Fuel Discount',  o.fuel_discount_pct > 0 ? `-${o.fuel_discount_pct}%` : 'None',
       'Account Balance', fmt$(o.balance));
    if (o.notes) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(`Notes: ${String(o.notes).substring(0, 130)}`, M, y);
      y += 5;
    }
  }

  // Fee Summary
  if (Object.keys(fs).length > 0) {
    section('FEE SUMMARY');
    kv('Total Movements',  fs.total_movements, 'Exempt Movements', fs.total_exempt);
    kv('Total Fees (net)', fmt$(fs.total_gross), '', '');
    if (fs.fuel_gallons > 0) {
      kv('Fuel Gallons', fmtGal(fs.fuel_gallons), 'After Discount', fmt$(fs.fuel_total_disc));
    }
  }

  // Movements
  if (data.movements?.length > 0) {
    section('RECENT MOVEMENTS');
    table(
      ['Type', 'Weight Class', 'Fee', 'Exempt', 'Timestamp'],
      data.movements.slice(0, 20).map(m => [
        m.movement_type,
        m.weight_class || '—',
        m.fee_exempt ? 'Exempt' : fmt$(m.fee),
        m.fee_exempt ? 'Yes' : 'No',
        fmtDT(m.timestamp),
      ]),
      [28, 32, 25, 20, 65]
    );
  }

  // Fuel
  if (data.source === 'registered' && data.fuel?.length > 0) {
    section('FUEL TRANSACTIONS');
    table(
      ['Source', 'Type', 'Gal', 'Unit $', 'Total', 'After Disc.', 'Date'],
      data.fuel.slice(0, 15).map(t => [
        t.source === 'truck' ? 'Truck' : 'Tank',
        t.fuel_type || '—',
        fmtGal(t.gallons),
        fmt$(t.price_per_gallon),
        fmt$(t.total_amount),
        fmt$(t.total_after_discount),
        fmtDT(t.timestamp),
      ]),
      [16, 16, 18, 20, 22, 24, 54]
    );
  } else if (data.source === 'visitor' && ac.fuel_added > 0) {
    section('FUEL');
    kv('Fuel Added', fmtGal(ac.fuel_added), 'Fuel Type', ac.fuel_type || '—');
  }

  // ============================================================
  // METTRE À JOUR LES HEADER/FOOTER SUR TOUTES LES PAGES
  // ============================================================
  const total = doc.internal.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawHeaderFooter(p, total);
  }

  // ============================================================
  // SAUVEGARDER
  // ============================================================
  const tail = (ac.tail_number || 'aircraft').replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`${tail}_${now.toISOString().split('T')[0]}.pdf`);
};

// ─── EXPORT EXCEL (inchangé, fonctionne déjà) ─────────────────────────────────

export const exportExcel = (data, company) => {
  const now  = new Date();
  const ac   = data.aircraft    || {};
  const o    = data.owner       || {};
  const fs   = data.fee_summary || {};

  const rows = [];

  rows.push([company.name || 'FBO', '', 'Aircraft Detail Report']);
  rows.push([[company.icao_code, company.address, company.city, company.phone, company.email].filter(Boolean).join('  ·  ')]);
  rows.push([`Generated by AeroTrick — ${fmtDT(now)}`]);
  rows.push([]);

  rows.push(['AIRCRAFT']);
  rows.push(['Tail Number', ac.tail_number, 'Status', data.source === 'registered' ? 'Registered' : 'Visitor']);
  rows.push(['Type', ac.aircraft_type || '', 'Manufacturer', ac.manufacturer || '']);
  rows.push(['Model', ac.model || '', 'Engine', ac.engine_type || '']);
  rows.push(['MTOW (lbs)', ac.mtow_kg ? Math.round(Number(ac.mtow_kg)).toLocaleString() : '', 'Weight Class', ac.weight_class || '']);
  rows.push(['Callsign', ac.callsign || '', 'Home Base', ac.home_base || '']);
  if (data.source === 'visitor') {
    rows.push(['Operator', ac.operator_name || '', 'Fuel Added', fmtGal(ac.fuel_added)]);
    rows.push(['First Seen', fmtDT(ac.first_seen), 'Last Seen', fmtDT(ac.last_seen)]);
  }
  rows.push([]);

  if (data.source === 'registered' && Object.keys(o).length > 0) {
    rows.push(['OWNER / CLIENT']);
    const name = o.company || `${o.first_name || ''} ${o.last_name || ''}`.trim();
    rows.push(['Name', name, 'Email', o.email || '']);
    rows.push(['Phone', o.phone_mobile || o.phone || '', 'Location', [o.city, o.state].filter(Boolean).join(', ')]);
    rows.push(['Landing Fee', o.charge_landing_fees ? 'Charged' : 'EXEMPT', 'Takeoff Fee', o.charge_takeoff_fees ? 'Charged' : 'EXEMPT']);
    rows.push(['Fuel Discount', o.fuel_discount_pct > 0 ? `-${o.fuel_discount_pct}%` : 'None', 'Balance', fmt$(o.balance)]);
    if (o.notes) rows.push(['Notes', o.notes]);
    rows.push([]);
  }

  if (Object.keys(fs).length > 0) {
    rows.push(['FEE SUMMARY']);
    rows.push(['Total Movements', fs.total_movements || 0, 'Exempt', fs.total_exempt || 0]);
    rows.push(['Total Fees (net)', fmt$(fs.total_gross)]);
    if (fs.fuel_gallons > 0) rows.push(['Fuel (gal)', fmtGal(fs.fuel_gallons), 'After Discount', fmt$(fs.fuel_total_disc)]);
    rows.push([]);
  }

  if (data.movements?.length > 0) {
    rows.push(['MOVEMENTS']);
    rows.push(['Type', 'Weight Class', 'Fee (net)', 'Exempt', 'Timestamp']);
    data.movements.forEach(m => rows.push([
      m.movement_type,
      m.weight_class || '—',
      m.fee_exempt ? 'Exempt' : fmt$(m.fee),
      m.fee_exempt ? 'Yes' : 'No',
      fmtDT(m.timestamp),
    ]));
    rows.push([]);
  }

  if (data.source === 'registered' && data.fuel?.length > 0) {
    rows.push(['FUEL TRANSACTIONS']);
    rows.push(['Source', 'Type', 'Gallons', 'Unit Price', 'Total', 'After Discount', 'Date']);
    data.fuel.forEach(t => rows.push([
      t.source === 'truck' ? 'Truck' : 'Tank',
      t.fuel_type || '—',
      fmtGal(t.gallons),
      fmt$(t.price_per_gallon),
      fmt$(t.total_amount),
      fmt$(t.total_after_discount),
      fmtDT(t.timestamp),
    ]));
    rows.push([]);
  } else if (data.source === 'visitor' && ac.fuel_added > 0) {
    rows.push(['FUEL']);
    rows.push(['Fuel Added', fmtGal(ac.fuel_added), 'Type', ac.fuel_type || '—']);
    rows.push([]);
  }

  rows.push([`© ${now.getFullYear()} AeroTrick — Generated for ${company.name || 'FBO'} — Confidential`]);

  const BOM = '\uFEFF';
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const tail = (ac.tail_number || 'aircraft').replace(/[^a-zA-Z0-9]/g, '_');
  link.href     = url;
  link.download = `${tail}_${now.toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── MAIN TRIGGER ─────────────────────────────────────────────────────────────

export const triggerExport = async (type, data) => {
  const res     = await axios.get(`${API_BASE}/company/settings`);
  const company = res.data || {};

  if (company.logo_url) {
    company._logoBase64 = await logoToBase64(company.logo_url);
  }

  if (type === 'pdf') {
    await exportPDF(data, company);
  } else {
    exportExcel(data, company);
  }
};